import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import API_BASE from '../config';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getOpenChatContactId } from '../utils/openChatContact';
import { messagePreview, senderName } from '../utils/messagePreview';

type UnreadEntry = {
  count: number;
  sender?: { id?: number; firstName?: string; lastName?: string; username?: string };
  lastMessage?: { id?: number; content?: string | null; mediaType?: string | null; createdAt?: string };
};

type Snapshot = Record<string, { count: number; lastId: number | null }>;

function totalFromCounts(data: Record<string, UnreadEntry>): number {
  return Object.values(data).reduce((sum, item) => sum + (Number(item?.count) || 0), 0);
}

function toSnapshot(data: Record<string, UnreadEntry>): Snapshot {
  const snap: Snapshot = {};
  for (const [id, entry] of Object.entries(data)) {
    snap[id] = {
      count: Number(entry?.count) || 0,
      lastId: entry?.lastMessage?.id != null ? Number(entry.lastMessage.id) : null,
    };
  }
  return snap;
}

/**
 * Polls unread message counts and shows an in-app toast when a new message arrives.
 * Also broadcasts badge updates for the sidebar.
 */
const IncomingMessagesWatcher: React.FC = () => {
  const { user } = useAuth();
  const toast = useToast();
  const location = useLocation();
  const prevRef = useRef<Snapshot | null>(null);
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;

  useEffect(() => {
    if (!user?.id) {
      prevRef.current = null;
      return;
    }

    let cancelled = false;

    const poll = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/messages/unread/counts`, {
          credentials: 'include',
        });
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as Record<string, UnreadEntry>;
        const snap = toSnapshot(data);
        const total = totalFromCounts(data);

        window.dispatchEvent(
          new CustomEvent('messagesUnreadUpdated', { detail: { total, counts: data } })
        );

        const prev = prevRef.current;
        prevRef.current = snap;
        // First successful poll only seeds baseline - don't toast existing unread.
        if (!prev) return;

        const onMessagesPage = pathnameRef.current.startsWith('/messages');
        const openId = getOpenChatContactId();

        for (const [senderKey, entry] of Object.entries(data)) {
          const senderId = Number(senderKey);
          const before = prev[senderKey];
          const lastId = entry?.lastMessage?.id != null ? Number(entry.lastMessage.id) : null;
          const isNew =
            !before ||
            entry.count > before.count ||
            (lastId != null && lastId !== before.lastId);

          if (!isNew) continue;
          // Already reading this thread - Messages page polls + marks read.
          if (openId != null && openId === senderId) continue;
          if (onMessagesPage && openId === senderId) continue;

          const name = senderName(entry.sender);
          const body = messagePreview(entry.lastMessage);
          toast.info(`${name}: ${body}`);
        }
      } catch {
        /* ignore transient network errors */
      }
    };

    poll();
    const id = window.setInterval(poll, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [user?.id, toast]);

  return null;
};

export default IncomingMessagesWatcher;
